import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  Fab,
  Tooltip 
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  TextFields, 
  Image, 
  ViewModule,
  Title,
  SmartButton,
  HorizontalRule,
  Height,
  Code
} from '@mui/icons-material';
import useEmailEditorStore from '../../utilities/emailEditorStore';
import { BLOCK_TYPES } from '../../utilities/blockTypes';
import EmailEditorBlock from './EmailEditorBlock';
function ColumnBlock({ id, type, properties, children = [] }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const { addBlock, selectBlock, selectedBlockId, removeBlock } = useEmailEditorStore();
  const isSelected = selectedBlockId === id;

  const handleAddClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAddChild = (childType) => {
    addBlock(childType, id);
    handleClose();
  };

  const handleColumnClick = (event) => {
    event.stopPropagation();
    selectBlock(id);
  };

  const handleDeleteColumn = (event) => {
    event.stopPropagation();
    removeBlock(id);
  };

  const blockTypes = [
    { 
      type: BLOCK_TYPES.HEADER, 
      label: 'Header Block', 
      icon: <Title /> 
    },
    { 
      type: BLOCK_TYPES.TEXT, 
      label: 'Text Block', 
      icon: <TextFields /> 
    },
    { 
      type: BLOCK_TYPES.IMAGE, 
      label: 'Image Block', 
      icon: <Image /> 
    },
    { 
      type: BLOCK_TYPES.BUTTON, 
      label: 'Button Block', 
      icon: <SmartButton /> 
    },
    { 
      type: BLOCK_TYPES.DIVIDER, 
      label: 'Divider Block', 
      icon: <HorizontalRule /> 
    },
    { 
      type: BLOCK_TYPES.SPACER, 
      label: 'Spacer Block', 
      icon: <Height /> 
    },
    { 
      type: BLOCK_TYPES.HTMLCONTENT, 
      label: 'HTML Content Block', 
      icon: <Code /> 
    }
  ];

  const isEmpty = !children || children.length === 0;

  // Convert padding object to CSS string
  const getPaddingStyle = (padding) => {
    if (typeof padding === 'object') {
      return `${padding.top || 0}px ${padding.right || 0}px ${padding.bottom || 0}px ${padding.left || 0}px`;
    }
    return padding || '10px';
  };

  // Convert margin object to CSS string
  const getMarginStyle = (margin) => {
    if (typeof margin === 'object') {
      return `${margin.top || 0}px ${margin.right || 0}px ${margin.bottom || 0}px ${margin.left || 0}px`;
    }
    return margin || '0px';
  };

  // Convert border object to CSS string
  const getBorderStyle = (border) => {
    if (typeof border === 'object') {
      return `${border.width || 0}px ${border.style || 'solid'} ${border.color || '#000000'}`;
    }
    return border || 'none';
  };

  return (
    <Box
      onClick={handleColumnClick}
      sx={{
        backgroundColor: properties.backgroundColor || 'transparent',
        padding: getPaddingStyle(properties.padding),
        margin: getMarginStyle(properties.margin),
        borderRadius: `${properties.borderRadius || 0}px`,
        border: getBorderStyle(properties.border),
        minHeight: `${properties.minHeight || 50}px`,
        width: '100%',
        maxWidth: properties.maxWidth || '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: properties.direction || 'column',
        justifyContent: properties.justifyContent || 'flex-start',
        alignItems: properties.alignItems || 'stretch',
        gap: `${properties.gap || 10}px`,
        flexGrow: properties.flexGrow || 1,
        flexShrink: properties.flexShrink || 1,
        flexBasis: properties.flexBasis || '0%',
        cursor: 'pointer',
        outline: isSelected ? '2px solid #1976d2' : 'none',
        outlineOffset: '2px',
        transition: 'outline 0.2s ease-in-out',
        '&:hover': {
          outline: isSelected ? '2px solid #1976d2' : '1px dashed #ccc',
        },
        '&:hover .column-action-buttons': {
          opacity: 1
        }
      }}
    >
      {isEmpty ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: `${properties.minHeight || 50}px`,
            flexGrow: 1,
            border: '2px dashed #e0e0e0',
            borderRadius: '4px',
            color: '#666',
            backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.05)' : 'transparent'
          }}
        >
          <Typography variant="body2" color="textSecondary" align="center">
            Column Block ({properties.width || '50%'})
            <br />
            Click + to add content
          </Typography>
        </Box>
      ) : (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: properties.direction || 'column',
            gap: `${properties.gap || 10}px`,
            width: '100%',
            height: '100%'
          }}
        >
          {children.map((child,index) => (
           <EmailEditorBlock
                    key={index}
                    blockType={child.type}
                    blockProperties={child.properties}
                    blockId={child.id}
                    blockParentId= {child.parentId}
                    blockChildren={child.children}
                    />

          ))}
        </Box>
      )}

      {/* Action Buttons Container */}
      <Box
        className="column-action-buttons"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          gap: 1,
          opacity: 0,
          transition: 'opacity 0.2s ease-in-out',
          zIndex: 10,
        }}
      >
        {/* Add Button */}
        <Tooltip title="Add content to column">
          <Fab
            size="small"
            color="primary"
            onClick={handleAddClick}
            sx={{
              width: 32,
              height: 32,
              minHeight: 32,
              '&:hover': {
                opacity: 1
              }
            }}
          >
            <AddIcon fontSize="small" />
          </Fab>
        </Tooltip>

        {/* Delete Button */}
        <Tooltip title="Delete column">
          <Fab
            size="small"
            color="error"
            onClick={handleDeleteColumn}
            sx={{
              width: 32,
              height: 32,
              minHeight: 32,
              '&:hover': {
                opacity: 1
              }
            }}
          >
            <DeleteIcon fontSize="small" />
          </Fab>
        </Tooltip>
      </Box>

      {/* Width indicator badge */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            left: 8,
            backgroundColor: '#1976d2',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            zIndex: 11
          }}
        >
          {properties.width || '50%'}
        </Box>
      )}

      {/* Block Type Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            maxHeight: 400
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {blockTypes.map((blockType) => (
          <MenuItem
            key={blockType.type}
            onClick={() => handleAddChild(blockType.type)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1
            }}
          >
            {blockType.icon}
            <Typography variant="body2">{blockType.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

// Child block renderer that renders the appropriate block component
function ChildBlockRenderer({ block }) {
  const { type, properties, id } = block;
  const { selectBlock, selectedBlockId, removeBlock } = useEmailEditorStore();
  const isSelected = selectedBlockId === id;

  const handleBlockClick = (event) => {
    event.stopPropagation();
    selectBlock(id);
  };

  const handleDeleteBlock = (event) => {
    event.stopPropagation();
    removeBlock(id);
  };

  // Convert padding object to CSS string
  const getPaddingStyle = (padding) => {
    if (typeof padding === 'object') {
      return `${padding.top || 0}px ${padding.right || 0}px ${padding.bottom || 0}px ${padding.left || 0}px`;
    }
    return padding || '8px';
  };

  // Convert margin object to CSS string
  const getMarginStyle = (margin) => {
    if (typeof margin === 'object') {
      return `${margin.top || 0}px ${margin.right || 0}px ${margin.bottom || 0}px ${margin.left || 0}px`;
    }
    return margin || '0px';
  };

  // Convert border object to CSS string
  const getBorderStyle = (border) => {
    if (typeof border === 'object') {
      return `${border.width || 0}px ${border.style || 'solid'} ${border.color || '#000000'}`;
    }
    return border || 'none';
  };

  const baseStyles = {
    padding: getPaddingStyle(properties.padding),
    margin: getMarginStyle(properties.margin),
    backgroundColor: properties.backgroundColor || 'transparent',
    borderRadius: `${properties.borderRadius || 0}px`,
    border: getBorderStyle(properties.border),
    cursor: 'pointer',
    outline: isSelected ? '2px solid #1976d2' : 'none',
    outlineOffset: '2px',
    transition: 'outline 0.2s ease-in-out',
    position: 'relative',
    '&:hover': {
      outline: isSelected ? '2px solid #1976d2' : '1px dashed #ccc',
    },
    '&:hover .block-delete-button': {
      opacity: 1
    }
  };

  const renderDeleteButton = () => (
    <Tooltip title="Delete block">
      <IconButton
        className="block-delete-button"
        size="small"
        color="error"
        onClick={handleDeleteBlock}
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          opacity: isSelected ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out',
          backgroundColor: 'white',
          boxShadow: 1,
          width: 24,
          height: 24,
          zIndex: 12,
          '&:hover': {
            backgroundColor: '#ffebee',
            opacity: 1
          }
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  switch (type) {
    case BLOCK_TYPES.HEADER:
      return (
        <Box onClick={handleBlockClick} sx={baseStyles}>
          {renderDeleteButton()}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontFamily: properties.titleFontFamily || 'Arial, sans-serif',
              fontSize: `${properties.titleFontSize || 28}px`,
              fontWeight: properties.titleFontWeight || 'bold',
              color: properties.titleColor || '#000000',
              textAlign: properties.textAlign || 'center',
              lineHeight: properties.titleLineHeight || 1.2,
              margin: 0
            }}
          >
            {properties.title || 'Header Title'}
          </Typography>
          {properties.subtitle && (
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: properties.subtitleFontFamily || 'Arial, sans-serif',
                fontSize: `${properties.subtitleFontSize || 16}px`,
                fontWeight: properties.subtitleFontWeight || 'normal',
                color: properties.subtitleColor || '#666666',
                textAlign: properties.textAlign || 'center',
                lineHeight: properties.subtitleLineHeight || 1.4,
                marginTop: `${properties.spacing || 10}px`
              }}
            >
              {properties.subtitle}
            </Typography>
          )}
        </Box>
      );
    
    case BLOCK_TYPES.TEXT:
      return (
        <Box onClick={handleBlockClick} sx={baseStyles}>
          {renderDeleteButton()}
          <Typography
            sx={{
              fontFamily: properties.fontFamily || 'Arial, sans-serif',
              fontSize: `${properties.fontSize || 16}px`,
              fontWeight: properties.fontWeight || 'normal',
              color: properties.color || '#000000',
              textAlign: properties.textAlign || 'left',
              lineHeight: properties.lineHeight || 1.5,
              textDecoration: properties.textDecoration || 'none'
            }}
            dangerouslySetInnerHTML={{ __html: properties.content || 'Enter your text here' }}
          />
        </Box>
      );
    
    case BLOCK_TYPES.IMAGE:
      return (
        <Box onClick={handleBlockClick} sx={baseStyles}>
          {renderDeleteButton()}
          {properties.src ? (
            <img
              src={properties.src}
              alt={properties.alt || 'Image'}
              style={{
                width: properties.width || '100%',
                height: properties.height || 'auto',
                objectFit: properties.objectFit || 'cover',
                display: 'block',
                maxWidth: '100%',
                borderRadius: `${properties.borderRadius || 0}px`
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '200px',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #ccc',
                borderRadius: '4px'
              }}
            >
              <Typography variant="body2" color="textSecondary">
                No image selected
              </Typography>
            </Box>
          )}
        </Box>
      );
    
    case BLOCK_TYPES.BUTTON:
      return (
        <Box 
          onClick={handleBlockClick} 
          sx={{
            ...baseStyles,
            textAlign: properties.textAlign || 'center'
          }}
        >
          {renderDeleteButton()}
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              padding: '12px 24px',
              backgroundColor: properties.backgroundColor || '#007bff',
              color: properties.color || '#ffffff',
              textDecoration: 'none',
              borderRadius: `${properties.borderRadius || 4}px`,
              fontFamily: properties.fontFamily || 'Arial, sans-serif',
              fontSize: `${properties.fontSize || 16}px`,
              fontWeight: properties.fontWeight || 'bold',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease',
              '&:hover': {
                backgroundColor: properties.hoverBackgroundColor || '#0056b3'
              }
            }}
          >
            {properties.text || 'Click Me'}
          </Box>
        </Box>
      );
    
    case BLOCK_TYPES.DIVIDER:
      return (
        <Box 
          onClick={handleBlockClick} 
          sx={{
            ...baseStyles,
            height: `${properties.height || 1}px`,
            backgroundColor: properties.backgroundColor || '#cccccc',
            width: properties.width || '100%',
            borderStyle: properties.style || 'solid',
            minHeight: isSelected ? '20px' : `${properties.height || 1}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {renderDeleteButton()}
          {isSelected && (
            <Typography variant="caption" color="textSecondary" sx={{ position: 'absolute' }}>
              Divider
            </Typography>
          )}
        </Box>
      );
    
    case BLOCK_TYPES.SPACER:
      return (
        <Box 
          onClick={handleBlockClick} 
          sx={{
            ...baseStyles,
            height: `${properties.height || 20}px`,
            width: properties.width || '100%',
            backgroundColor: properties.backgroundColor || 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '20px',
            border: isSelected ? '1px dashed #ccc' : 'none'
          }}
        >
          {renderDeleteButton()}
          {isSelected && (
            <Typography variant="caption" color="textSecondary">
              Spacer ({properties.height || 20}px)
            </Typography>
          )}
        </Box>
      );

    case BLOCK_TYPES.HTMLCONTENT:
      return (
        <Box 
          onClick={handleBlockClick} 
          sx={baseStyles}
          dangerouslySetInnerHTML={{ 
            __html: properties.htmlContent || '<p>Enter your HTML content here</p>' 
          }}
        >
          {renderDeleteButton()}
        </Box>
      );
    
    default:
      return (
        <Box onClick={handleBlockClick} sx={baseStyles}>
          {renderDeleteButton()}
          <Typography variant="body2" color="textSecondary">
            Unknown block type: {type}
          </Typography>
        </Box>
      );
  }
}

export default ColumnBlock;